import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Calendar, Upload, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUploader = ({ images, onChange, single = false }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const newImages = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.url) {
          newImages.push(`${BACKEND_URL}${data.url}`);
        }
      } catch (err) {
        toast.error(`Napaka pri nalaganju: ${file.name}`);
      }
    }

    if (single) {
      onChange(newImages[0] || '');
    } else {
      onChange([...(images || []), ...newImages]);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    if (single) {
      onChange('');
    } else {
      const updated = [...(images || [])];
      updated.splice(index, 1);
      onChange(updated);
    }
  };

  const imageList = single ? (images ? [images] : []) : (images || []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {imageList.map((img, idx) => (
          <div key={idx} className="relative group">
            <img
              src={img}
              alt={`Slika ${idx + 1}`}
              className="w-24 h-24 object-cover rounded-lg border border-stone-700"
            />
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || (single && imageList.length > 0)}
          className="w-24 h-24 border-2 border-dashed border-stone-600 hover:border-emerald-500 rounded-lg flex flex-col items-center justify-center text-stone-400 hover:text-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 mb-1" />
              <span className="text-xs">Naloži</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={!single}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title_sl: '',
    title_en: '',
    excerpt_sl: '',
    excerpt_en: '',
    content_sl: '',
    content_en: '',
    category: 'news',
    image: '',
    images: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      toast.error('Napaka pri nalaganju novic');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNews(null);
    setFormData({
      title_sl: '',
      title_en: '',
      excerpt_sl: '',
      excerpt_en: '',
      content_sl: '',
      content_en: '',
      category: 'news',
      image: '',
      images: []
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      title_sl: item.title_sl,
      title_en: item.title_en,
      excerpt_sl: item.excerpt_sl,
      excerpt_en: item.excerpt_en,
      content_sl: item.content_sl,
      content_en: item.content_en,
      category: item.category,
      image: item.image || '',
      images: item.images || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to novico?')) return;
    
    try {
      await axios.delete(`${API}/news/${id}`);
      toast.success('Novica izbrisana');
      fetchNews();
    } catch (error) {
      toast.error('Napaka pri brisanju');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingNews) {
        await axios.put(`${API}/news/${editingNews.id}`, formData);
        toast.success('Novica posodobljena');
      } else {
        await axios.post(`${API}/news`, formData);
        toast.success('Novica ustvarjena');
      }
      setIsDialogOpen(false);
      fetchNews();
    } catch (error) {
      toast.error('Napaka pri shranjevanju');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sl-SI');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-stone-400">{news.length} novic</p>
        <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-500">
          <Plus className="w-5 h-5 mr-2" />
          Nova novica
        </Button>
      </div>

      {/* News List */}
      <div className="space-y-4">
        {news.map((item) => (
          <Card key={item.id} className="bg-stone-800/50 border-stone-700 p-4">
            <div className="flex items-start justify-between gap-4">
              {/* Thumbnail */}
              {item.image && (
                <img
                  src={item.image}
                  alt={item.title_sl}
                  className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <Badge variant="outline" className={item.category === 'event' ? 'border-amber-500 text-amber-400' : 'border-emerald-500 text-emerald-400'}>
                    {item.category === 'event' ? 'Dogodek' : 'Novica'}
                  </Badge>
                  <span className="text-stone-500 text-sm flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(item.date)}
                  </span>
                  {(item.images?.length > 0) && (
                    <span className="text-stone-500 text-sm flex items-center gap-1">
                      <ImageIcon className="w-4 h-4" />
                      {item.images.length} slik
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title_sl}</h3>
                <p className="text-stone-400 text-sm line-clamp-2">{item.excerpt_sl}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="text-stone-400 hover:text-white">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {news.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            Ni novic. Kliknite &quot;Nova novica&quot; za dodajanje.
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl bg-stone-900 border-stone-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingNews ? 'Uredi novico' : 'Nova novica'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Naslov (SL)</Label>
                <Input
                  value={formData.title_sl}
                  onChange={(e) => setFormData({...formData, title_sl: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Naslov (EN)</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Kategorija</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full bg-stone-800 border border-stone-700 text-white rounded-md px-3 py-2"
              >
                <option value="news">Novica</option>
                <option value="event">Dogodek</option>
              </select>
            </div>

            {/* Main Image */}
            <div className="space-y-2">
              <Label className="text-stone-300">Naslovna slika</Label>
              <ImageUploader
                images={formData.image}
                onChange={(img) => setFormData({...formData, image: img})}
                single={true}
              />
            </div>

            {/* Gallery Images */}
            <div className="space-y-2">
              <Label className="text-stone-300">Galerija slik (neobvezno)</Label>
              <ImageUploader
                images={formData.images}
                onChange={(imgs) => setFormData({...formData, images: imgs})}
                single={false}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Povzetek (SL)</Label>
                <Textarea
                  value={formData.excerpt_sl}
                  onChange={(e) => setFormData({...formData, excerpt_sl: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Povzetek (EN)</Label>
                <Textarea
                  value={formData.excerpt_en}
                  onChange={(e) => setFormData({...formData, excerpt_en: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={3}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Vsebina (SL)</Label>
                <Textarea
                  value={formData.content_sl}
                  onChange={(e) => setFormData({...formData, content_sl: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={5}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Vsebina (EN)</Label>
                <Textarea
                  value={formData.content_en}
                  onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={5}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-stone-400">
                Prekliči
              </Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-500">
                {saving ? 'Shranjujem...' : 'Shrani'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNews;
